import { useEffect, useRef, useState } from 'react'
import type { AiArticlesResponse } from '../types/news'
import { fetchCurrentArticles, startArticleFreshness } from '../services/articleFreshness'

function readEmbeddedArticleData(): AiArticlesResponse | null {
  const node = document.getElementById('omikina-article-data')
  if (!node?.textContent) return null
  try {
    const payload = JSON.parse(node.textContent) as AiArticlesResponse
    return Array.isArray(payload.articles) && payload.articles.length > 0 ? payload : null
  } catch {
    return null
  }
}

export interface CompanyArticleState {
  data: AiArticlesResponse | null
  loading: boolean
  error: string | null
}

export function useCompanyArticles(enabled = true): CompanyArticleState {
  const [data, setData] = useState<AiArticlesResponse | null>(() => readEmbeddedArticleData())
  const [loading, setLoading] = useState(enabled && data === null)
  const [error, setError] = useState<string | null>(null)
  const hasImmediateData = useRef(data !== null)

  useEffect(() => {
    if (!enabled) return
    let disposed = false
    if (!hasImmediateData.current) queueMicrotask(() => { if (!disposed) setLoading(true) })
    const freshness = startArticleFreshness({
      environment: {
        windowEvents: window, documentEvents: document,
        isVisible: () => document.visibilityState === 'visible', now: Date.now,
        setInterval: (callback, delay) => window.setInterval(callback, delay),
        clearInterval: handle => window.clearInterval(handle),
        setTimeout: (callback, delay) => window.setTimeout(callback, delay),
        clearTimeout: handle => window.clearTimeout(handle),
      },
      load: fetchCurrentArticles,
      onData: payload => {
        hasImmediateData.current = true
        // This endpoint returns the complete authoritative archive, including
        // revised media and withdrawals. Do not keep removed embedded stories.
        setData(payload)
        setError(null)
      },
      onError: () => setError(hasImmediateData.current
        ? 'Showing the last available articles. Updates are temporarily unavailable; we’ll retry automatically.'
        : 'AI COMPANY UPDATES TEMPORARILY UNAVAILABLE'),
      onSettled: () => setLoading(false),
    })
    return () => { disposed = true; freshness.dispose() }
  }, [enabled])

  return { data, loading: enabled && loading, error }
}
